import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { supabase, apiCall } from '../supabase';

const C = {
  black:  '#09080c',
  card:   '#15121e',
  raised: '#1d1929',
  violet: '#5a3d99',
  accent: '#7c5cbf',
  lilac:  '#c9a0e8',
  ivory:  '#f2ece0',
  muted:  'rgba(242,236,224,0.55)',
  faint:  'rgba(242,236,224,0.08)',
  border: 'rgba(242,236,224,0.07)',
  gold:   '#e2b96a',
};
const font = {
  display: "'DM Serif Display', serif",
  body:    "'Playfair Display', serif",
  ui:      "'Inter', sans-serif",
};
const BG = 'linear-gradient(160deg, #0d0b14 0%, #110d1a 40%, #160e20 70%, #0d0b14 100%)';

const TIER_NAMES = {
  whisper:   'Whisper',
  desire:    'Desire',
  obsession: 'Obsession',
};
const TIER_PRICES = {
  whisper:   '$9.99',
  desire:    '$19.99',
  obsession: '$44.99',
};

export default function Account({ user }) {
  const navigate  = useNavigate();
  const location  = useLocation();
  const justSubscribed = new URLSearchParams(location.search).get('subscribed') === 'true';

  const [subscription, setSubscription] = useState(null);
  const [loading, setLoading]           = useState(true);
  const [portalLoading, setPortalLoading] = useState(false);

  // Password change state
  const [showPwForm, setShowPwForm]   = useState(false);
  const [newPw, setNewPw]             = useState('');
  const [pwMsg, setPwMsg]             = useState('');
  const [pwLoading, setPwLoading]     = useState(false);

  useEffect(() => {
    apiCall('/api/subscription/status')
      .then(data => setSubscription(data))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  const handlePortal = async () => {
    setPortalLoading(true);
    try {
      const data = await apiCall('/api/subscription/portal', { method: 'POST' });
      if (data.url) window.location.href = data.url;
    } catch (e) {
      console.error(e);
    } finally {
      setPortalLoading(false);
    }
  };

  const handleChangePassword = async () => {
    if (!newPw || newPw.length < 8) { setPwMsg('Password must be at least 8 characters.'); return; }
    setPwLoading(true); setPwMsg('');
    const { error } = await supabase.auth.updateUser({ password: newPw });
    setPwLoading(false);
    if (error) { setPwMsg(error.message); }
    else { setPwMsg('Password updated ✓'); setNewPw(''); setTimeout(() => { setShowPwForm(false); setPwMsg(''); }, 1500); }
  };

  const handleSignOut = async () => {
    await supabase.auth.signOut();
    navigate('/');
  };

  const isActive = subscription?.status === 'active';
  const tierName  = TIER_NAMES[subscription?.tier] || 'Desire';
  const tierPrice = TIER_PRICES[subscription?.tier] || '$19.99';

  // ── POST-PAYMENT ONBOARDING SCREEN ──────────────────────────────────────────
  if (justSubscribed && !loading) {
    return (
      <div style={{ minHeight: '100vh', background: BG, color: C.ivory, fontFamily: font.ui, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', padding: '40px 24px', textAlign: 'center' }}>
        <style>{`
          @keyframes fadeUp { from{opacity:0;transform:translateY(16px)} to{opacity:1;transform:translateY(0)} }
          @keyframes glow   { 0%,100%{opacity:0.6} 50%{opacity:1} }
        `}</style>

        {/* Ambient glow */}
        <div style={{ position: 'fixed', width: '600px', height: '600px', borderRadius: '50%', background: 'radial-gradient(circle, rgba(90,61,153,0.18) 0%, transparent 70%)', top: '-100px', left: '50%', transform: 'translateX(-50%)', pointerEvents: 'none' }} />

        <div style={{ position: 'relative', zIndex: 1, maxWidth: '380px', animation: 'fadeUp 0.6s ease both' }}>
          {/* Logo */}
          <img src="/logo.png" alt="" style={{ width: '100px', marginBottom: '32px', filter: 'drop-shadow(0 0 32px rgba(90,61,153,0.9))', animation: 'glow 2.5s ease-in-out infinite' }} />

          {/* Headline */}
          <div style={{ fontFamily: font.display, fontStyle: 'italic', fontSize: '36px', lineHeight: 1.2, marginBottom: '10px' }}>
            Welcome to<br />YourSiren
          </div>

          {/* Plan badge */}
          <div style={{ display: 'inline-flex', alignItems: 'center', gap: '8px', background: 'rgba(90,61,153,0.25)', border: '1px solid rgba(124,92,191,0.4)', borderRadius: '30px', padding: '8px 18px', marginBottom: '20px' }}>
            <div style={{ width: '7px', height: '7px', borderRadius: '50%', background: C.lilac, boxShadow: `0 0 8px ${C.lilac}` }} />
            <span style={{ fontSize: '13px', color: C.lilac, fontWeight: 500 }}>{tierName} · {tierPrice}/mo</span>
          </div>

          <div style={{ fontSize: '15px', color: C.muted, lineHeight: 1.7, marginBottom: '36px' }}>
            Your subscription is active.<br />Every story is written and narrated just for you.
          </div>

          {/* CTA */}
          <button
            onClick={() => navigate('/studio')}
            style={{ width: '100%', padding: '17px', borderRadius: '14px', border: 'none', cursor: 'pointer', background: 'linear-gradient(135deg, #5a3d99, #7c5cbf)', color: C.ivory, fontFamily: font.display, fontStyle: 'italic', fontSize: '20px', boxShadow: '0 6px 28px rgba(90,61,153,0.5)', marginBottom: '14px' }}
          >
            Begin your first story →
          </button>

          <button
            onClick={() => navigate('/account', { replace: true })}
            style={{ background: 'none', border: 'none', color: C.muted, fontSize: '12px', cursor: 'pointer', fontFamily: font.ui, letterSpacing: '0.5px' }}
          >
            View account
          </button>
        </div>
      </div>
    );
  }

  // ── MAIN ACCOUNT PAGE ───────────────────────────────────────────────────────
  return (
    <div style={{ minHeight: '100vh', background: BG, color: C.ivory, fontFamily: font.ui, fontWeight: 300, paddingBottom: '80px' }}>
      <style>{`@keyframes fadeUp{from{opacity:0;transform:translateY(10px)}to{opacity:1;transform:translateY(0)}}`}</style>

      {/* Header */}
      <div style={{ padding: '24px 22px 20px', borderBottom: `1px solid ${C.border}` }}>
        <button onClick={() => navigate('/studio')} style={{ background: 'none', border: 'none', color: C.muted, fontSize: '11px', cursor: 'pointer', letterSpacing: '1px', marginBottom: '16px', display: 'block', fontFamily: font.ui }}>← Back</button>
        <div style={{ fontFamily: font.display, fontStyle: 'italic', fontSize: '32px' }}>Account</div>
        <div style={{ fontSize: '12px', color: C.muted, marginTop: '4px' }}>{user?.email}</div>
      </div>

      <div style={{ padding: '24px 22px', display: 'flex', flexDirection: 'column', gap: '12px', maxWidth: '480px', margin: '0 auto' }}>

        {/* ── Subscription card ── */}
        <div style={{ background: C.card, border: `1px solid ${isActive ? 'rgba(124,92,191,0.3)' : C.border}`, borderRadius: '20px', padding: '22px', animation: 'fadeUp 0.4s ease both' }}>
          <div style={{ fontSize: '9px', letterSpacing: '4px', textTransform: 'uppercase', color: C.accent, opacity: 0.7, marginBottom: '12px' }}>Subscription</div>

          {loading ? (
            <div style={{ color: C.muted, fontSize: '13px' }}>Loading…</div>
          ) : isActive ? (
            <>
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '4px' }}>
                <div style={{ width: '8px', height: '8px', borderRadius: '50%', background: C.lilac, boxShadow: `0 0 8px ${C.lilac}` }} />
                <div style={{ fontFamily: font.body, fontStyle: 'italic', fontSize: '18px' }}>Active — {tierName}</div>
              </div>
              <div style={{ fontSize: '12px', color: C.muted, marginBottom: '18px', lineHeight: 1.6 }}>
                {tierPrice}/month · {subscription?.storyLimit >= 99 ? 'Unlimited' : subscription?.storyLimit} stories/month
              </div>

              <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                {/* Change plan */}
                <button onClick={() => navigate('/subscribe')} style={{ flex: 1, minWidth: '120px', padding: '11px 14px', background: 'transparent', border: `1px solid rgba(124,92,191,0.3)`, borderRadius: '10px', color: C.lilac, fontSize: '12px', cursor: 'pointer', fontFamily: font.ui }}>
                  Change plan
                </button>
                {/* Cancel / manage billing */}
                <button onClick={handlePortal} disabled={portalLoading} style={{ flex: 1, minWidth: '120px', padding: '11px 14px', background: 'transparent', border: `1px solid ${C.border}`, borderRadius: '10px', color: C.muted, fontSize: '12px', cursor: 'pointer', fontFamily: font.ui }}>
                  {portalLoading ? '…' : 'Cancel / billing'}
                </button>
              </div>
            </>
          ) : (
            <>
              <div style={{ fontFamily: font.body, fontStyle: 'italic', fontSize: '16px', marginBottom: '6px', color: C.muted }}>No active subscription</div>
              <div style={{ fontSize: '12px', color: 'rgba(242,236,224,0.2)', marginBottom: '16px' }}>Subscribe to unlock unlimited stories.</div>
              <button onClick={() => navigate('/subscribe')} style={{ padding: '12px 24px', background: 'linear-gradient(135deg, #5a3d99, #7c5cbf)', border: 'none', borderRadius: '10px', color: C.ivory, fontFamily: font.display, fontStyle: 'italic', fontSize: '16px', cursor: 'pointer', boxShadow: '0 6px 20px rgba(90,61,153,0.3)' }}>
                Subscribe — from $9.99/mo
              </button>
            </>
          )}
        </div>

        {/* ── Change password ── */}
        <div style={{ background: C.card, border: `1px solid ${C.border}`, borderRadius: '20px', padding: '22px', animation: 'fadeUp 0.5s ease both' }}>
          <div style={{ fontSize: '9px', letterSpacing: '4px', textTransform: 'uppercase', color: C.accent, opacity: 0.7, marginBottom: '12px' }}>Security</div>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <div>
              <div style={{ fontSize: '13px', color: C.ivory, marginBottom: '2px' }}>Password</div>
              <div style={{ fontSize: '11px', color: C.muted }}>Change your account password</div>
            </div>
            <button onClick={() => { setShowPwForm(!showPwForm); setPwMsg(''); setNewPw(''); }} style={{ padding: '9px 16px', background: 'transparent', border: `1px solid ${C.border}`, borderRadius: '9px', color: C.muted, fontSize: '12px', cursor: 'pointer', fontFamily: font.ui }}>
              {showPwForm ? 'Cancel' : 'Change'}
            </button>
          </div>

          {showPwForm && (
            <div style={{ marginTop: '16px', animation: 'fadeUp 0.3s ease both' }}>
              <input
                type="password"
                value={newPw}
                onChange={e => setNewPw(e.target.value)}
                placeholder="New password (min 8 chars)…"
                style={{ width: '100%', padding: '12px 14px', background: 'rgba(255,255,255,0.05)', border: `1px solid ${C.border}`, borderRadius: '10px', color: C.ivory, fontSize: '13px', fontFamily: font.ui, outline: 'none', boxSizing: 'border-box', marginBottom: '10px' }}
              />
              {pwMsg && (
                <div style={{ fontSize: '12px', marginBottom: '10px', color: pwMsg.includes('✓') ? C.lilac : '#f08080' }}>{pwMsg}</div>
              )}
              <button onClick={handleChangePassword} disabled={pwLoading} style={{ width: '100%', padding: '12px', background: 'linear-gradient(135deg, #5a3d99, #7c5cbf)', border: 'none', borderRadius: '10px', color: C.ivory, fontSize: '13px', cursor: 'pointer', fontFamily: font.ui }}>
                {pwLoading ? 'Updating…' : 'Update password'}
              </button>
            </div>
          )}
        </div>

        {/* ── Email row ── */}
        <div style={{ background: C.card, border: `1px solid ${C.border}`, borderRadius: '16px', padding: '16px 20px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', animation: 'fadeUp 0.6s ease both' }}>
          <div style={{ fontSize: '11px', letterSpacing: '2px', textTransform: 'uppercase', color: 'rgba(242,236,224,0.2)', fontFamily: font.ui }}>Email</div>
          <div style={{ fontSize: '13px', color: C.muted, fontFamily: font.body, fontStyle: 'italic' }}>{user?.email}</div>
        </div>

        {/* ── Sign out ── */}
        <button onClick={handleSignOut} style={{ width: '100%', padding: '14px', background: 'transparent', border: `1px solid ${C.border}`, borderRadius: '13px', color: 'rgba(242,236,224,0.25)', fontSize: '12px', letterSpacing: '2px', textTransform: 'uppercase', cursor: 'pointer', fontFamily: font.ui, marginTop: '4px', animation: 'fadeUp 0.7s ease both' }}>
          Sign out
        </button>
      </div>
    </div>
  );
}
